// apps/web/src/pages/Admin/CollectionCompletionFields.jsx
//
// Admin "Completion requirement" controls: Complete all titles (default) or
// Complete a minimum number ("any 5 of 8"). Validation here is for friendly
// feedback only — the backend validates again and is authoritative
// (collectionCompletionService.parseCompletionInput).
import React from 'react';
import styles from './AdminCollections.module.css';

export const EMPTY_COMPLETION = Object.freeze({ mode: 'ALL_REQUIRED', requiredCount: '' });

/** Form state from an API collection row (snake or camel case). */
export function completionFromCollection(collection) {
    const type = collection?.completion_type ?? collection?.completionType;
    if (type !== 'MINIMUM_COUNT') return { ...EMPTY_COMPLETION };
    const n = collection.required_count ?? collection.requiredCount;
    return { mode: 'MINIMUM_COUNT', requiredCount: n == null ? '' : String(n) };
}

/** Distinct entries, mirroring the server's identity rule (movie / TV season / series). */
export function countEntries(items = []) {
    const keys = new Set();
    for (const item of items) {
        const tmdb = item.tmdb_id ?? item.tmdbId;
        if (tmdb === '' || tmdb == null) continue;
        const season = item.season_number ?? item.seasonNumber;
        const kind = item.kind === 'tv' ? (season !== '' && season != null ? `tv_season:${season}` : 'tv_series') : 'movie';
        keys.add(`${kind}:${Number(tmdb)}`);
    }
    return keys.size;
}

export function validateCompletion(form, entryCount) {
    if (form.mode !== 'MINIMUM_COUNT') return null;
    const n = Number(form.requiredCount);
    if (form.requiredCount === '' || !Number.isInteger(n)) return 'Enter how many titles are required to unlock.';
    if (n < 1) return 'At least 1 title must be required.';
    if (n > entryCount) return `Can't require ${n} — the collection has ${entryCount} title${entryCount === 1 ? '' : 's'}.`;
    return null;
}

export function completionToPayload(form) {
    return form.mode === 'MINIMUM_COUNT'
        ? { completion_type: 'MINIMUM_COUNT', required_count: Number(form.requiredCount) }
        : { completion_type: 'ALL_REQUIRED', required_count: null };
}

export function CollectionCompletionFields({ value, onChange, entryCount, disabled = false, idPrefix = 'completion' }) {
    const minimum = value.mode === 'MINIMUM_COUNT';
    const problem = minimum ? validateCompletion(value, entryCount) : null;
    const n = Number(value.requiredCount);

    return (
        <fieldset className={styles.availabilityFieldset} disabled={disabled}>
            <legend className={styles.itemsTitle}>Completion requirement</legend>
            <div className={styles.availabilityModes} role="radiogroup" aria-label="Completion requirement">
                <label className={styles.toggleLabel}>
                    <input
                        type="radio"
                        name={`${idPrefix}-mode`}
                        checked={!minimum}
                        onChange={() => onChange({ ...value, mode: 'ALL_REQUIRED' })}
                    />
                    <span>Complete all titles</span>
                </label>
                <label className={styles.toggleLabel}>
                    <input
                        type="radio"
                        name={`${idPrefix}-mode`}
                        checked={minimum}
                        onChange={() => onChange({
                            ...value,
                            mode: 'MINIMUM_COUNT',
                            requiredCount: value.requiredCount || String(Math.max(1, entryCount - 1)),
                        })}
                    />
                    <span>Complete a minimum number</span>
                </label>
            </div>

            {minimum && (
                <div className={styles.coinFormGrid}>
                    <div className={styles.itemField}>
                        <label className={styles.labelSmall} htmlFor={`${idPrefix}-count`}>Titles required to unlock</label>
                        <input
                            id={`${idPrefix}-count`}
                            className={styles.inputSmall}
                            type="number"
                            min="1"
                            max={entryCount || undefined}
                            value={value.requiredCount}
                            onChange={(e) => onChange({ ...value, requiredCount: e.target.value })}
                        />
                    </div>
                    <p className={styles.itemsHint} data-testid="completion-summary">
                        {problem || (
                            <>
                                <strong>{n} of {entryCount}</strong> titles required — users may verify any {n}.
                                {n === entryCount && ' (Same as "Complete all titles".)'}
                            </>
                        )}
                    </p>
                </div>
            )}
            {!minimum && (
                <p className={styles.itemsHint}>Users must verify all {entryCount} title{entryCount === 1 ? '' : 's'}.</p>
            )}
        </fieldset>
    );
}

export default CollectionCompletionFields;
